import { values as LIBELLES } from '../../../contexts/LanguageContext'
import {
  COULEURS, RATIO_LOGO, RATIO_SIGNATURE, formaterDate, couper, capitales,
} from '../Certificat/modeleCertificat'
import { GRILLE_R_PIXEL } from '../Formats/ChargementPixelR'
import { choisirTexte, FORME_TU, FORME_VOUS } from '../../../utils/formeAdresse'
import { SEGMENTS_JAUGE } from './contenuBilan'

/* ⛔ BILAN SOFT SKILLS : LES PAGES (07/10/2026, etape 1, MAQUETTE : aucun
   ecran ne les affiche encore). v2 du 07/10, decisions de Nawal :

   - un document A PARTAGER (LinkedIn), dont l'apprenant est fier : ce qui se
     partage ne montre QUE des forces. Donc DEUX pages A4 portrait :
       PAGE 1, LA VITRINE : en-tete, phrase d'accomplissement, les 3 atouts
         avec leur jauge, « Ce que tu as gagné », le tampon, la signature, la
         mention. Ni amorce, ni conseil, ni competence a consolider ;
       PAGE 2, « PRÉPARE TON ENTRETIEN », pour l'apprenant seul : l'amorce et
         une phrase CV par atout, puis « Ta prochaine marche » (la competence
         a consolider, avec sa jauge et son conseil) ;
     et une IMAGE DE PARTAGE 4:5 (1080 x 1350 px), recomposee pour ce format
     (`composerPartage`), pas un recadrage de la page 1 ;
   - v3 du 07/10 : une page « POUR UN EMPLOYEUR » (`composerEmployeur`), a la
     troisieme personne, sans rien de ce qui reste a travailler ;
   - la version CLAIRE est retenue (la noire reste disponible) ;
   - un TAMPON « attesté par Rising Up » remplace le badge a coche : double
     cercle, texte sur le cercle, R pixel au centre, orange en aplat, incline
     de 10 degres, vectoriel, pose pres de la signature ;
   - la JAUGE des ecrans Diagnostic remplace les traits orange : memes
     couleurs (contenuBilan.js, jaugeDuScore), sans chiffre ;
   - plus de « regard sur soi » chiffre : un texte fixe (dictionnaire).

   MEMES CODES ET MEMES FONCTIONS QUE LE CERTIFICAT : couleurs, date,
   coupure des lignes, capitales, proportions du logo et de la signature
   (Certificat/modeleCertificat.js), mesure des textes (Certificat/mesure.js),
   dessin PDF (Certificat/pdfCertificat.js) ; meme description des pages en
   elements simples (rect, trace, texte, image), en millimetres. Le R pixel
   est celui de la plateforme (Formats/ChargementPixelR.js).
   Questrial 400 seule, Burnt Orange seul accent de la charte, coins carres,
   aucun degrade ni ombre, aucun tiret cadratin. Seules couleurs hors charte :
   celles de la jauge, reprises telles quelles des ecrans Diagnostic.

   La page s'adapte a la longueur des textes : si tout ne tient pas, les
   corps de texte diminuent par paliers (`densite`, 1 a 0,8) ; au-dela,
   `deborde` est vrai et le bilan ne doit pas etre remis tel quel. */

export const PAGE_BILAN = { largeur: 210, hauteur: 297 }
/* 4:5 ; rendue a 127 dpi : 1080 x 1350 px */
export const PAGE_PARTAGE = { largeur: 216, hauteur: 270 }

/* la charte (guide de style v1.0) : les couleurs du certificat, plus les
   trois de sa variante claire (etape 1) */
const CHARTE = { ...COULEURS, cremeClair: '#FFFCF8', texteDoux: '#5A544D', bord: '#E8E1D6' }

export const VARIANTES_BILAN = {
  claire: {
    fond: CHARTE.cremeClair, heros: CHARTE.encre, texte: CHARTE.texte, doux: CHARTE.texteDoux, muet: CHARTE.texteMuet,
    filet: CHARTE.bord, coinTrait: CHARTE.encre, coinFond: CHARTE.cremeChaud,
    carte: CHARTE.blanc, cadre: CHARTE.bord, encadre: CHARTE.cremeChaud,
    logo: 'noir', signature: 'noire',
  },
  noire: {
    fond: CHARTE.encre, heros: CHARTE.blanc, texte: CHARTE.cremeChaud, doux: CHARTE.texteMuet, muet: CHARTE.texteMuet,
    filet: CHARTE.texte, coinTrait: CHARTE.cremeChaud, coinFond: null,
    carte: null, cadre: CHARTE.texte, encadre: null,
    logo: 'blanc', signature: 'blanche',
  },
}

/* images/logoNoir-pdf.png : 374 x 70 px (le blanc : RATIO_LOGO, 428 x 80) */
export const RATIO_LOGO_NOIR = 374 / 70

/* la jauge : les segments VIDES des ecrans Diagnostic (#EFEFEF) et leur pas,
   10 de segment pour 8 de gouttiere (FormatDiag.js) */
export const JAUGE_VIDE = '#EFEFEF'
const RAPPORT_GOUTTIERE = 8 / 10

export const TAMPON = { inclinaison: -10 }

/* Questrial, a la louche, pour placer les lignes : hauteur des capitales et
   jambage, en fraction du corps. */
const CAPITALE = 0.72
const JAMBAGE = 0.24
const SENTINELLE = '\ue000'
const DENSITES = [1, 0.95, 0.9, 0.85, 0.8]
const ETIQUETTE = 2.35, ESP_ETIQUETTE = 0.14

/* tu ou vous : le mecanisme publie (utils/formeAdresse.js), et lui seul ;
   la forme vient du public du contenu (etudiant = tu, professionnel = vous) */
const libelle = (cle, langue, forme) => choisirTexte(LIBELLES?.bilanSoftSkills?.[cle], langue, forme) ?? LIBELLES?.bilanSoftSkills?.[cle]?.fr ?? ''
const libelleCertificat = (cle, langue) => LIBELLES?.certificatReussite?.[cle]?.[langue] ?? LIBELLES?.certificatReussite?.[cle]?.fr ?? ''

/* ----------------------------------------------------- les primitives
   les memes que le certificat, plus le texte incline (`angle`) du tampon */
const outils = (mesurer) => {
  const largeur = (t, taille, esp = 0) => {
    const n = Array.from(String(t)).length
    return mesurer(String(t), taille) + esp * taille * Math.max(0, n - 1)
  }
  const fabrique = (liste) => ({
    texte: (t, { x, y, taille, couleur, esp = 0, ancre = 'gauche', role, angle }) => {
      const l = largeur(t, taille, esp)
      const gauche = ancre === 'milieu' ? x - l / 2 : ancre === 'droite' ? x - l : x
      const el = { type: 'texte', texte: String(t), x: gauche, y, taille, couleur, espacement: esp * taille, largeur: l, role }
      if (angle) el.angle = angle
      liste.push(el)
      return l
    },
    rect: (x, y, l, h, couleur, role) => liste.push({ type: 'rect', x, y, l, h, couleur, role }),
    trace: (boucles, couleur, role) => liste.push({ type: 'trace', boucles, couleur, role, y: 0 }),
    image: (source, x, y, l, h, role) => liste.push({ type: 'image', source, x, y, l, h, role }),
  })
  /* `couper` (certificat) coupe sur tout blanc, insecable compris : on la
     protege le temps de la coupure, pour que « : » ne parte pas seul a la
     ligne. */
  const lignesDe = (t, taille, esp, largeurMax) => {
    const protege = String(t || '').replace(/\u00a0/g, SENTINELLE)
    const mesure = (s, tt, ee) => largeur(s.replace(/\ue000/g, '\u00a0'), tt, ee)
    return couper(protege, taille, esp, largeurMax, mesure).map((l) => l.replace(/\ue000/g, '\u00a0'))
  }
  /* un paragraphe dont le HAUT des capitales est en `haut` ; rend son bas */
  const paragraphe = (D, t, { x, haut, largeurMax, taille, couleur, interligne = 1.42, esp = 0, role, ancre = 'gauche' }) => {
    const lignes = lignesDe(t, taille, esp, largeurMax)
    const y0 = haut + CAPITALE * taille
    lignes.forEach((l, i) => D.texte(l, { x, y: y0 + i * taille * interligne, taille, couleur, esp, role, ancre }))
    return y0 + (lignes.length - 1) * taille * interligne + JAMBAGE * taille
  }
  const decaler = (liste, dy) => liste.forEach((el) => {
    el.y += dy
    if (el.boucles) el.boucles = el.boucles.map((b) => b.map(([x, y]) => [x, y + dy]))
  })
  return { largeur, fabrique, lignesDe, paragraphe, decaler }
}

/* ------------------------------------------------- les pieces communes */
const fondEtCoins = (P, T, page) => {
  P.rect(0, 0, page.largeur, page.hauteur, T.fond, 'fond')
  /* coins carres en L, haut a gauche et bas a droite (valeurs du
     certificat : marge 11, cote 14, trait 0,8) ; claire : carre creme */
  const M = 11, C = 14, E = 0.8
  const xb = page.largeur - M - C, yb = page.hauteur - M - C
  if (T.coinFond) {
    P.rect(M, M, C, C, T.coinFond, 'coin-fond')
    P.rect(xb, yb, C, C, T.coinFond, 'coin-fond')
  }
  P.rect(M, M, C, E, T.coinTrait, 'coin')
  P.rect(M, M, E, C, T.coinTrait, 'coin')
  P.rect(xb, yb + C - E, C, E, T.coinTrait, 'coin')
  P.rect(xb + C - E, yb, E, C, T.coinTrait, 'coin')
}

const enTeteCommun = (P, T, page, { exemple, LC, caps, hLogo = 5.2, yLogo = 15, droite }) => {
  const ratio = T.logo === 'noir' ? RATIO_LOGO_NOIR : RATIO_LOGO
  P.image('logo', droite - hLogo * ratio, yLogo, hLogo * ratio, hLogo, 'logo')
  if (exemple) {
    P.texte(caps(LC('exemple')), { x: page.largeur / 2, y: yLogo + 4, taille: 2.3, couleur: T.muet, esp: ESP_ETIQUETTE, ancre: 'milieu', role: 'exemple' })
  }
}

/* la jauge : 20 segments, les remplis de la couleur du palier */
const dessinerJauge = (D, { x, y, largeur, hauteur, jauge }) => {
  const n = SEGMENTS_JAUGE
  const l = largeur / (n + (n - 1) * RAPPORT_GOUTTIERE)
  const e = l * RAPPORT_GOUTTIERE
  const remplis = Math.max(0, Math.min(n, jauge?.remplis || 0))
  for (let s = 0; s < n; s++) {
    const plein = s < remplis
    D.rect(x + s * (l + e), y, l, hauteur, plein ? jauge.couleur : JAUGE_VIDE, plein ? 'jauge-plein' : 'jauge-vide')
  }
}

/* Le contour d'un ensemble de cases d'une grille, en boucles fermees (repris
   de l'etape 1 du certificat, 390a619) : un seul trace, sans les fines
   coutures que les visionneuses PDF laissent entre deux rectangles. */
const contourDesCases = (cases) => {
  const cotes = new Map()
  const cle = (a, b) => a.join(',') + '>' + b.join(',')
  const ajouter = (a, b) => {
    const inverse = cle(b, a)
    if (cotes.has(inverse)) cotes.delete(inverse)
    else cotes.set(cle(a, b), [a, b])
  }
  cases.forEach(([r, c]) => {
    ajouter([c, r], [c + 1, r]); ajouter([c + 1, r], [c + 1, r + 1])
    ajouter([c + 1, r + 1], [c, r + 1]); ajouter([c, r + 1], [c, r])
  })
  const parDepart = new Map()
  cotes.forEach(([a, b]) => {
    const k = a.join(',')
    if (!parDepart.has(k)) parDepart.set(k, [])
    parDepart.get(k).push([a, b])
  })
  const boucles = []
  const prendre = (k) => {
    const liste = parDepart.get(k)
    const cote = liste && liste.pop()
    if (liste && liste.length === 0) parDepart.delete(k)
    return cote
  }
  while (parDepart.size) {
    const depart = parDepart.keys().next().value
    const boucle = []
    let cote = prendre(depart)
    while (cote) {
      boucle.push(cote[0])
      const k = cote[1].join(',')
      if (k === depart) break
      cote = prendre(k)
    }
    boucles.push(boucle)
  }
  return boucles
}

const cercle = (cx, cy, r, n = 240) => Array.from({ length: n }, (_, i) => {
  const a = (2 * Math.PI * i) / n
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)]
})
/* rotation dans le repere de la page (y vers le bas) : degres positifs =
   sens des aiguilles d'une montre */
const tourner = ([x, y], cx, cy, deg) => {
  const a = (deg * Math.PI) / 180
  const dx = x - cx, dy = y - cy
  return [cx + dx * Math.cos(a) - dy * Math.sin(a), cy + dx * Math.sin(a) + dy * Math.cos(a)]
}

/* LE TAMPON « ATTESTÉ PAR RISING UP ». Deux anneaux, un texte en haut du
   cercle (lu de gauche a droite), un en bas (lu de gauche a droite, pieds
   vers l'exterieur), deux points de separation, le R pixel au centre. Tout
   en aplat orange, sans effet d'encre ; incline de `inclinaison` degres.
   Le texte est un vrai texte PDF, lettre par lettre (angle de chaque lettre). */
const dessinerTampon = (O, D, { cx, cy, rayon, haut, bas, couleur = CHARTE.orange, inclinaison = TAMPON.inclinaison }) => {
  const R = rayon
  const ext = [R, R * 0.945]
  const int = [R * 0.7, R * 0.672]
  D.trace([cercle(cx, cy, ext[0]), cercle(cx, cy, ext[1])], couleur, 'tampon-anneau')
  D.trace([cercle(cx, cy, int[0]), cercle(cx, cy, int[1])], couleur, 'tampon-anneau')
  const rMilieu = (ext[1] + int[0]) / 2
  const bande = ext[1] - int[0]
  const ESP = 0.1
  const ARC_HAUT = (172 * Math.PI) / 180
  const ARC_BAS = (150 * Math.PI) / 180
  /* corps commun aux deux textes, reduit jusqu'a ce que le haut tienne dans
     172 degres et le bas dans 150 (v4 : « PROFIL ÉVALUÉ PAR RISING UP » est
     plus long que l'ancien texte) */
  const arc = (t, taille) => O.largeur(t, taille, ESP) / rMilieu
  let taille = Math.min(bande * 0.62 / CAPITALE, R * 0.2)
  while (taille > 1 && (arc(haut, taille) > ARC_HAUT || arc(bas, taille) > ARC_BAS)) taille -= 0.05
  const capH = CAPITALE * taille
  const tilt = (inclinaison * Math.PI) / 180
  const ecrire = (texte, enHaut) => {
    const lettres = Array.from(texte)
    const avances = lettres.map((ch, i) => O.largeur(ch, taille) + (i < lettres.length - 1 ? ESP * taille : 0))
    const total = avances.reduce((a, b) => a + b, 0)
    const sens = enHaut ? 1 : -1
    const rBase = enHaut ? rMilieu - capH / 2 : rMilieu + capH / 2
    let phi = (enHaut ? -Math.PI / 2 : Math.PI / 2) - sens * (total / rMilieu) / 2 + tilt
    lettres.forEach((ch, i) => {
      const w = O.largeur(ch, taille)
      const phiC = phi + sens * (w / 2) / rMilieu
      const alpha = enHaut ? phiC + Math.PI / 2 : phiC - Math.PI / 2
      const px = cx + rBase * Math.cos(phiC), py = cy + rBase * Math.sin(phiC)
      const lx = px - (w / 2) * Math.cos(alpha), ly = py - (w / 2) * Math.sin(alpha)
      if (ch.trim()) D.texte(ch, { x: lx, y: ly, taille, couleur, role: 'tampon-texte', angle: -(alpha * 180) / Math.PI })
      phi += sens * avances[i] / rMilieu
    })
  }
  ecrire(haut, true)
  ecrire(bas, false)
  ;[0, Math.PI].forEach((a) => {
    const x = cx + rMilieu * Math.cos(a + tilt), y = cy + rMilieu * Math.sin(a + tilt)
    D.trace([cercle(x, y, R * 0.032, 48)], couleur, 'tampon-point')
  })
  /* le R pixel, centre sur ses cases pleines */
  const cases = []
  GRILLE_R_PIXEL.forEach((ligne, r) => ligne.forEach((plein, c) => { if (plein) cases.push([r, c]) }))
  const cMin = Math.min(...cases.map(([, c]) => c)), cMax = Math.max(...cases.map(([, c]) => c))
  const rMin = Math.min(...cases.map(([r]) => r)), rMax = Math.max(...cases.map(([r]) => r))
  const maille = R * 0.058
  const lR = (cMax - cMin + 1) * maille, hR = (rMax - rMin + 1) * maille
  const boucles = contourDesCases(cases.map(([r, c]) => [r - rMin, c - cMin]))
    .map((b) => b.map(([c, r]) => tourner([cx - lR / 2 + c * maille, cy - hR / 2 + r * maille], cx, cy, inclinaison)))
  D.trace(boucles, couleur, 'tampon-r')
}

/* v4 : LES DEUX MENTIONS DU PIED (methode, puis « pas une certification de
   niveau de maîtrise »), centrees, calees en bas de la page ; rend le haut
   des capitales de la premiere ligne */
/* des lignes de longueurs voisines (pas de mot seul sur la derniere ligne),
   sans en ajouter une */
const lignesEquilibrees = (O, texte, taille, largeurMax) => {
  const n = O.lignesDe(texte, taille, 0, largeurMax).length
  if (n < 2) return O.lignesDe(texte, taille, 0, largeurMax)
  let w = O.largeur(texte, taille) / n
  let lignes = O.lignesDe(texte, taille, 0, w)
  while (lignes.length > n && w < largeurMax) { w += 1; lignes = O.lignesDe(texte, taille, 0, w) }
  return lignes
}

const mentionsPied = (O, P, T, L, page, { largeurMax = 150, taille = 2.35, bas = page.hauteur - 10 } = {}) => {
  const IL = 1.35
  const lignes = lignesEquilibrees(O, L('mentionMethode'), taille, largeurMax)
  const tout = [...lignes, L('mentionCertification')]
  const y0 = bas - (tout.length - 1) * taille * IL
  tout.forEach((l, i) => P.texte(l, { x: page.largeur / 2, y: y0 + i * taille * IL, taille, couleur: T.doux, ancre: 'milieu', role: i < lignes.length ? 'mention-methode' : 'mention-certification' }))
  return y0 - CAPITALE * taille
}

/* signature de Nawal : image, filet, nom et fonction, ceux du certificat
   (« Dr Nawal Abboub », publie le 07/10) */
const blocSignature = (P, T, LC, { x, bas, lSig = 32, k = 1 }) => {
  const hSig = lSig / RATIO_SIGNATURE
  const yLigne = bas - 10 * k
  P.image('signature', x - lSig / 2, yLigne - hSig + 1.5 * k, lSig, hSig, 'signature')
  P.rect(x - 25 * k, yLigne, 50 * k, 0.25, T.filet, 'ligne-signature')
  P.texte(LC('signataireNom'), { x, y: bas - 4.6 * k, taille: 3.4 * k, couleur: T.heros, ancre: 'milieu', role: 'signataire' })
  P.texte(LC('signataireFonction'), { x, y: bas, taille: 2.6 * k, couleur: T.doux, ancre: 'milieu', role: 'fonction' })
}

/* Les blocs du contenu se placent entre le bas de l'en-tete et le haut du
   pied : ecarts egaux au-dessus, entre et sous les blocs, entre `min` et
   `max`. Sous `min`, la page deborde. */
const placerBlocs = (O, page, blocs, { haut, bas, min, max }) => {
  const total = blocs.reduce((s, b) => s + b.hauteur, 0)
  const libre = bas - haut - total
  const ecartBrut = libre / (blocs.length + 1)
  const ecart = Math.min(max, ecartBrut)
  const reste = Math.max(0, libre - ecart * (blocs.length + 1))
  let y = haut + ecart + reste / 2
  blocs.forEach((b) => {
    O.decaler(b.liste, y)
    page.push(...b.liste)
    y += b.hauteur + ecart
  })
  return { deborde: ecartBrut < min, basContenu: y - ecart }
}

/* Une ligne centree faite de morceaux (etiquette + valeur) : rend sa largeur. */
const ligneCentree = (O, P, morceaux, { cx, y, role }) => {
  const larg = morceaux.reduce((s, m) => s + (m.avant || 0) + O.largeur(m.t, m.taille, m.esp || 0), 0)
  let x = cx - larg / 2
  morceaux.forEach((m) => {
    x += m.avant || 0
    x += P.texte(m.t, { x, y, taille: m.taille, couleur: m.couleur, esp: m.esp || 0, role: m.role || role })
  })
  return larg
}

/* « PARCOURS ... DÉLIVRÉ LE ... » : une ligne centree, deux si elle ne tient
   pas ; rend la ligne de base de la derniere ligne */
const ligneParcoursEtDate = (O, P, { T, L, LC, caps, nomParcours, dateTexte, CX, LARG, basPhrase }) => {
  const tVal = 4.1
  const mParcours = [
    { t: caps(L('parcours')), taille: ETIQUETTE, couleur: T.doux, esp: ESP_ETIQUETTE, role: 'etiquette-parcours' },
    { t: nomParcours, taille: tVal, couleur: T.heros, esp: -0.02, avant: 2.6, role: 'parcours' },
  ]
  const mDate = [
    { t: caps(LC('delivreLe')), taille: ETIQUETTE, couleur: T.doux, esp: ESP_ETIQUETTE, role: 'etiquette-date' },
    { t: dateTexte, taille: tVal, couleur: T.heros, esp: -0.01, avant: 2.6, role: 'date' },
  ]
  const larg = (m) => m.reduce((s, x) => s + (x.avant || 0) + O.largeur(x.t, x.taille, x.esp || 0), 0)
  let yLigne = basPhrase + 8.5
  /* etape 2 : pas de titre du plan dans les donnees, pas de « PARCOURS » */
  if (!nomParcours) {
    ligneCentree(O, P, mDate, { cx: CX, y: yLigne })
    return yLigne
  }
  if (larg(mParcours) + 10 + larg(mDate) <= LARG) {
    ligneCentree(O, P, [...mParcours, { ...mDate[0], avant: 10 }, mDate[1]], { cx: CX, y: yLigne })
  } else {
    if (larg(mParcours) > LARG) {
      const place = LARG - O.largeur(mParcours[0].t, ETIQUETTE, ESP_ETIQUETTE) - 2.6
      const lignes = O.lignesDe(nomParcours, tVal, -0.02, place)
      mParcours[1].t = lignes.length > 1 ? lignes[0] + '…' : lignes[0]
    }
    ligneCentree(O, P, mParcours, { cx: CX, y: yLigne })
    yLigne += 6.5
    ligneCentree(O, P, mDate, { cx: CX, y: yLigne })
  }
  return yLigne
}

/* ------------------------------------------------- PAGE 1 : LA VITRINE */
const composerVitrine = (ctx, densite) => {
  const { O, T, L, LC, caps, prenomNom, nomParcours, dateTexte, contenu, exemple } = ctx
  const page = PAGE_BILAN
  const d = densite
  const els = []
  const P = O.fabrique(els)
  const CX = page.largeur / 2, X0 = 20, X1 = 190, LARG = X1 - X0
  fondEtCoins(P, T, page)
  enTeteCommun(P, T, page, { exemple, LC, caps, droite: X1 })

  P.texte(caps(L('mention')), { x: CX, y: 37, taille: 3.4, couleur: CHARTE.orange, esp: 0.14, ancre: 'milieu', role: 'mention' })
  /* le nom, heros de la page : reduit s'il le faut, deux lignes au plus */
  const LARGEUR_NOM = 166, ESP_NOM = -0.035
  let tNom = 15
  while (tNom > 10 && O.largeur(prenomNom, tNom, ESP_NOM) > LARGEUR_NOM) tNom -= 0.5
  let lignesNom = [String(prenomNom || '')]
  if (O.largeur(prenomNom, tNom, ESP_NOM) > LARGEUR_NOM) lignesNom = couper(prenomNom, tNom, ESP_NOM, LARGEUR_NOM, O.largeur).slice(0, 2)
  let yNom = 37 + 7.5 + CAPITALE * tNom
  lignesNom.forEach((l, i) => P.texte(l, { x: CX, y: yNom + i * tNom * 1.05, taille: tNom, couleur: T.heros, esp: ESP_NOM, ancre: 'milieu', role: 'nom' }))
  yNom += (lignesNom.length - 1) * tNom * 1.05
  const yFilet = yNom + 5.5
  P.rect(CX - 6, yFilet, 12, 0.8, CHARTE.orange, 'filet-nom')
  const basPhrase = O.paragraphe(P, L('accomplissement'), { x: CX, ancre: 'milieu', haut: yFilet + 6.5, largeurMax: 160, taille: 4.6, couleur: T.texte, interligne: 1.3, role: 'accomplissement' })

  const yLigne = ligneParcoursEtDate(O, P, { T, L, LC, caps, nomParcours, dateTexte, CX, LARG, basPhrase })
  const hautContenu = yLigne + 2

  const blocs = []
  const nouveau = () => { const liste = []; return { liste, D: O.fabrique(liste) } }

  /* les 3 atouts, en cartes, chacune avec sa jauge */
  {
    const { liste, D } = nouveau()
    const yEt = CAPITALE * ETIQUETTE
    const lEt = D.texte(caps(L('atouts')), { x: CX, y: yEt, taille: ETIQUETTE, couleur: CHARTE.orange, esp: ESP_ETIQUETTE, ancre: 'milieu', role: 'etiquette-atouts' })
    if (contenu.provisoire?.atouts) D.texte(L('provisoire'), { x: CX + lEt / 2 + 2.5, y: yEt, taille: ETIQUETTE, couleur: CHARTE.orange, esp: 0.04, role: 'provisoire' })
    let y = yEt + JAMBAGE * ETIQUETTE + 4.5
    const PAD = 5.5, LJ = 46, HJ = 4.4
    contenu.atouts.forEach((a, i) => {
      const carte = []
      const C = O.fabrique(carte)
      const tTitre = 5 * d
      const xT = X0 + PAD
      const lTitre = LARG - 2 * PAD - LJ - 8
      let yc = O.paragraphe(C, a.nomUsage, { x: xT, haut: PAD, largeurMax: lTitre, taille: tTitre, couleur: T.heros, interligne: 1.12, esp: -0.02, role: 'atout-titre' }) + 2
      C.texte(caps(a.nomCoreSkills), { x: xT, y: yc + CAPITALE * 2.2, taille: 2.2, couleur: T.doux, esp: 0.1, role: 'atout-coreskills' })
      yc += 2.2 + 3.4
      dessinerJauge(C, { x: X1 - PAD - LJ, y: PAD + (CAPITALE * tTitre - HJ) / 2, largeur: LJ, hauteur: HJ, jauge: a.jauge })
      yc = O.paragraphe(C, a.enSituation, { x: xT, haut: yc, largeurMax: LARG - 2 * PAD, taille: 3.35 * d, couleur: T.texte, interligne: 1.4, role: 'atout-situation' })
      const h = yc + PAD
      if (T.carte) {
        D.rect(X0, y, LARG, h, T.cadre, 'carte-cadre')
        D.rect(X0 + 0.25, y + 0.25, LARG - 0.5, h - 0.5, T.carte, 'carte')
      } else {
        D.rect(X0, y, LARG, 0.25, T.cadre, 'carte-cadre')
      }
      O.decaler(carte, y)
      liste.push(...carte)
      y += h + (i < 2 ? 3.5 : 0)
    })
    blocs.push({ liste, hauteur: y })
  }

  /* « Ce que tu as gagné » : encadre a bordure gauche orange (charte) */
  {
    const { liste, D } = nouveau()
    const PAD = 6
    const interne = []
    const I = O.fabrique(interne)
    let y = O.paragraphe(I, L('gagneTitre'), { x: X0 + PAD + 2, haut: PAD, largeurMax: LARG - 2 * PAD - 2, taille: 5 * d, couleur: T.heros, esp: -0.02, role: 'gagne-titre' }) + 3
    y = O.paragraphe(I, L('gagneTexte'), { x: X0 + PAD + 2, haut: y, largeurMax: LARG - 2 * PAD - 2, taille: 3.6 * d, couleur: T.texte, interligne: 1.42, role: 'gagne-texte' })
    const h = y + PAD
    if (T.encadre) D.rect(X0, 0, LARG, h, T.encadre, 'encadre-gagne')
    D.rect(X0, 0, 1.06, h, CHARTE.orange, 'barre-gagne')
    liste.push(...interne)
    blocs.push({ liste, hauteur: h })
  }

  /* pied : les deux mentions en bas ; au-dessus, le tampon, pose sur le
     debut du filet de signature (ni sur le nom, ni sur la fonction), et la
     signature */
  const hautMentions = mentionsPied(O, P, T, L, page)
  const BAS = hautMentions - 5
  const xSig = 118
  const RAYON = 16
  const cyTampon = BAS - 20
  const placement = placerBlocs(O, els, blocs, { haut: hautContenu, bas: cyTampon - RAYON - 5, min: 5, max: 13 })
  dessinerTampon(O, P, { cx: xSig - 34, cy: cyTampon, rayon: RAYON, haut: caps(L('tamponHaut')), bas: caps(L('tamponBas')) })
  blocSignature(P, T, LC, { x: xSig, bas: BAS })

  return { page, fond: T.fond, elements: els, densite, ...placement }
}

/* ------------------------------------- PAGE 2 : PRÉPARE TON ENTRETIEN */
const composerEntretien = (ctx, densite) => {
  const { O, T, L, LC, caps, prenomNom, nomParcours, contenu, exemple } = ctx
  const page = PAGE_BILAN
  const d = densite
  const els = []
  const P = O.fabrique(els)
  const X0 = 20, X1 = 190, LARG = X1 - X0
  fondEtCoins(P, T, page)
  enTeteCommun(P, T, page, { exemple, LC, caps, droite: X1 })

  P.texte(caps(L('mention')), { x: X0, y: 37, taille: 3, couleur: CHARTE.orange, esp: 0.14, role: 'mention' })
  const tTitre = 10
  const yTitre = 37 + 5 + CAPITALE * tTitre
  P.texte(L('entretienTitre'), { x: X0, y: yTitre, taille: tTitre, couleur: T.heros, esp: -0.03, role: 'entretien-titre' })
  const ySous = yTitre + 8
  const lNom = P.texte(prenomNom, { x: X0, y: ySous, taille: 3.8, couleur: T.heros, esp: -0.01, role: 'nom' })
  if (nomParcours) {
    const lEt = P.texte(caps(L('parcours')), { x: X0 + lNom + 7, y: ySous, taille: ETIQUETTE, couleur: T.doux, esp: ESP_ETIQUETTE, role: 'etiquette-parcours' })
    const xParc = X0 + lNom + 7 + lEt + 2.6
    const lignesParc = O.lignesDe(nomParcours, 3.8, -0.01, X1 - xParc)
    P.texte(lignesParc.length > 1 ? lignesParc[0] + '…' : lignesParc[0], { x: xParc, y: ySous, taille: 3.8, couleur: T.heros, esp: -0.01, role: 'parcours' })
  }
  const yFilet = ySous + 5.5
  P.rect(X0, yFilet, LARG, 0.25, T.filet, 'filet-entete')

  const blocs = []
  const nouveau = () => { const liste = []; return { liste, D: O.fabrique(liste) } }
  const LG = 50, XD = X0 + 58, LD = X1 - XD

  /* une rangee par atout : l'amorce, puis la phrase CV */
  contenu.atouts.forEach((a, i) => {
    const { liste, D } = nouveau()
    const PAD = 5
    let yG = O.paragraphe(D, a.nomUsage, { x: X0, haut: PAD, largeurMax: LG, taille: 4.6 * d, couleur: T.heros, interligne: 1.15, esp: -0.02, role: 'atout-titre' }) + 2
    D.texte(caps(a.nomCoreSkills), { x: X0, y: yG + CAPITALE * 2.2, taille: 2.2, couleur: T.doux, esp: 0.1, role: 'atout-coreskills' })
    yG += 2.2
    const et = (t, y, prov) => {
      const yy = y + CAPITALE * ETIQUETTE
      const l = D.texte(caps(t), { x: XD, y: yy, taille: ETIQUETTE, couleur: CHARTE.orange, esp: ESP_ETIQUETTE, role: 'etiquette-entretien' })
      if (prov) D.texte(L('provisoire'), { x: XD + l + 2.5, y: yy, taille: ETIQUETTE, couleur: CHARTE.orange, esp: 0.04, role: 'provisoire' })
      return yy + JAMBAGE * ETIQUETTE
    }
    let yD = et(L('prepareExemple'), PAD, contenu.provisoire?.atouts && i === 0) + 2.6
    yD = O.paragraphe(D, a.amorce, { x: XD, haut: yD, largeurMax: LD, taille: 3.3 * d, couleur: T.texte, role: 'atout-amorce' }) + 4
    yD = et(L('cv'), yD) + 2.6
    D.texte('→', { x: XD, y: yD + CAPITALE * 3.3 * d, taille: 3.3 * d, couleur: CHARTE.orange, role: 'cv-puce' })
    yD = O.paragraphe(D, a.phraseCv, { x: XD + 5.5, haut: yD, largeurMax: LD - 5.5, taille: 3.3 * d, couleur: T.heros, role: 'cv-phrase' })
    const h = Math.max(yG, yD) + PAD
    if (i > 0) D.rect(X0, 0, LARG, 0.25, T.filet, 'filet-rangee')
    blocs.push({ liste, hauteur: h })
  })

  /* « Ta prochaine marche » : la competence a consolider, presentee comme
     la suite, avec sa jauge et son conseil ; absente quand seules 3
     competences sont mesurees (etape 2) */
  if (contenu.aConsolider) {
    const { liste, D } = nouveau()
    const c = contenu.aConsolider
    const PAD = 6, LJ = 46, HJ = 4.4
    const xT = X0 + PAD + 2
    const interne = []
    const I = O.fabrique(interne)
    const yEt = PAD + CAPITALE * ETIQUETTE
    const lEt = I.texte(caps(L('prochaineMarche')), { x: xT, y: yEt, taille: ETIQUETTE, couleur: CHARTE.orange, esp: ESP_ETIQUETTE, role: 'etiquette-marche' })
    if (contenu.provisoire?.aConsolider) I.texte(L('provisoire'), { x: xT + lEt + 2.5, y: yEt, taille: ETIQUETTE, couleur: CHARTE.orange, esp: 0.04, role: 'provisoire' })
    let y = yEt + JAMBAGE * ETIQUETTE + 3.4
    const tTitre = 4.8 * d
    const yHautTitre = y
    y = O.paragraphe(I, c.nomUsage, { x: xT, haut: y, largeurMax: LARG - 2 * PAD - LJ - 10, taille: tTitre, couleur: T.heros, interligne: 1.12, esp: -0.02, role: 'marche-titre' }) + 2
    dessinerJauge(I, { x: X1 - PAD - LJ, y: yHautTitre + (CAPITALE * tTitre - HJ) / 2, largeur: LJ, hauteur: HJ, jauge: c.jauge })
    I.texte(caps(c.nomCoreSkills), { x: xT, y: y + CAPITALE * 2.2, taille: 2.2, couleur: T.doux, esp: 0.1, role: 'marche-coreskills' })
    y += 2.2 + 3.6
    y = O.paragraphe(I, c.conseil, { x: xT, haut: y, largeurMax: LARG - 2 * PAD - 2, taille: 3.4 * d, couleur: T.texte, role: 'marche-conseil' })
    const h = y + PAD
    if (T.encadre) D.rect(X0, 0, LARG, h, T.encadre, 'encadre-marche')
    D.rect(X0, 0, 1.06, h, CHARTE.orange, 'barre-marche')
    liste.push(...interne)
    blocs.push({ liste, hauteur: h })
  }

  const hautMentions = mentionsPied(O, P, T, L, page)
  const placement = placerBlocs(O, els, blocs, { haut: yFilet, bas: hautMentions - 6, min: 2.5, max: 9 })
  return { page, fond: T.fond, elements: els, densite, ...placement }
}

/* ------------------------------ « POUR UN EMPLOYEUR » : UNE PAGE, SOBRE
   v3 du 07/10 (Nawal) : a joindre a une candidature, a la TROISIEME
   personne. Par atout : nom d'usage, nom CoreSkills, jauge, la phrase « en
   situation » (sans pronom) et la premiere phrase CV. Tampon, signature,
   mention. Ni amorce, ni conseil, ni prochaine marche, ni chiffre.
   `emplacementQr` : l'emplacement reserve au futur QR code de verification,
   marque « [QR de vérification, à venir] ». A RETIRER tant que le QR
   n'existe pas : il n'est dessine que si on le demande (maquette). */
const composerPageEmployeur = (ctx, densite, { emplacementQr = false } = {}) => {
  const { O, T, L, LC, caps, prenomNom, nomParcours, dateTexte, contenu, exemple } = ctx
  const page = PAGE_BILAN
  const d = densite
  const els = []
  const P = O.fabrique(els)
  const CX = page.largeur / 2, X0 = 20, X1 = 190, LARG = X1 - X0
  fondEtCoins(P, T, page)
  enTeteCommun(P, T, page, { exemple, LC, caps, droite: X1 })

  P.texte(caps(L('mention')), { x: CX, y: 37, taille: 3.2, couleur: CHARTE.orange, esp: 0.14, ancre: 'milieu', role: 'mention' })
  const LARGEUR_NOM = 166, ESP_NOM = -0.035
  let tNom = 13
  while (tNom > 9 && O.largeur(prenomNom, tNom, ESP_NOM) > LARGEUR_NOM) tNom -= 0.5
  let lignesNom = [String(prenomNom || '')]
  if (O.largeur(prenomNom, tNom, ESP_NOM) > LARGEUR_NOM) lignesNom = couper(prenomNom, tNom, ESP_NOM, LARGEUR_NOM, O.largeur).slice(0, 2)
  let yNom = 37 + 7 + CAPITALE * tNom
  lignesNom.forEach((l, i) => P.texte(l, { x: CX, y: yNom + i * tNom * 1.05, taille: tNom, couleur: T.heros, esp: ESP_NOM, ancre: 'milieu', role: 'nom' }))
  yNom += (lignesNom.length - 1) * tNom * 1.05
  const yFilet = yNom + 5
  P.rect(CX - 6, yFilet, 12, 0.8, CHARTE.orange, 'filet-nom')
  const basPhrase = O.paragraphe(P, L('employeurPhrase'), { x: CX, ancre: 'milieu', haut: yFilet + 6, largeurMax: 160, taille: 4.4, couleur: T.texte, interligne: 1.3, role: 'employeur-phrase' })
  const yLigne = ligneParcoursEtDate(O, P, { T, L, LC, caps, nomParcours, dateTexte, CX, LARG, basPhrase })
  /* v5 : plus de ligne « Document à joindre ... » sous le parcours */
  const hautContenu = yLigne + 2

  const liste = []
  const D = O.fabrique(liste)
  /* v4 : « SES 3 SOFT SKILLS CLEFS » au-dessus des trois blocs */
  const yEt = CAPITALE * ETIQUETTE
  const lEt = D.texte(caps(L('atoutsEmployeur')), { x: CX, y: yEt, taille: ETIQUETTE, couleur: CHARTE.orange, esp: ESP_ETIQUETTE, ancre: 'milieu', role: 'etiquette-atouts' })
  if (contenu.provisoire?.atouts) D.texte(L('provisoire'), { x: CX + lEt / 2 + 2.5, y: yEt, taille: ETIQUETTE, couleur: CHARTE.orange, esp: 0.04, role: 'provisoire' })
  let y = yEt + JAMBAGE * ETIQUETTE + 4.5
  const PAD = 5.5, LJ = 46, HJ = 4.4
  contenu.atouts.forEach((a, i) => {
    const carte = []
    const C = O.fabrique(carte)
    const tTitre = 5 * d
    const xT = X0 + PAD
    let yc = O.paragraphe(C, a.nomUsage, { x: xT, haut: PAD, largeurMax: LARG - 2 * PAD - LJ - 8, taille: tTitre, couleur: T.heros, interligne: 1.12, esp: -0.02, role: 'atout-titre' }) + 2
    C.texte(caps(a.nomCoreSkills), { x: xT, y: yc + CAPITALE * 2.2, taille: 2.2, couleur: T.doux, esp: 0.1, role: 'atout-coreskills' })
    yc += 2.2 + 3.4
    dessinerJauge(C, { x: X1 - PAD - LJ, y: PAD + (CAPITALE * tTitre - HJ) / 2, largeur: LJ, hauteur: HJ, jauge: a.jauge })
    yc = O.paragraphe(C, a.enSituation, { x: xT, haut: yc, largeurMax: LARG - 2 * PAD, taille: 3.3 * d, couleur: T.texte, interligne: 1.4, role: 'atout-situation' }) + 3
    C.texte('\u2192', { x: xT, y: yc + CAPITALE * 3.3 * d, taille: 3.3 * d, couleur: CHARTE.orange, role: 'cv-puce' })
    yc = O.paragraphe(C, a.phraseCv, { x: xT + 5.5, haut: yc, largeurMax: LARG - 2 * PAD - 5.5, taille: 3.3 * d, couleur: T.heros, role: 'cv-phrase' })
    const h = yc + PAD
    if (T.carte) {
      D.rect(X0, y, LARG, h, T.cadre, 'carte-cadre')
      D.rect(X0 + 0.25, y + 0.25, LARG - 0.5, h - 0.5, T.carte, 'carte')
    } else {
      D.rect(X0, y, LARG, 0.25, T.cadre, 'carte-cadre')
    }
    O.decaler(carte, y)
    liste.push(...carte)
    y += h + (i < 2 ? 3.5 : 0)
  })

  const hautMentions = mentionsPied(O, P, T, L, page)
  const BAS = hautMentions - 5
  const xSig = 140
  const RAYON = 16
  const cyTampon = BAS - 20
  const placement = placerBlocs(O, els, [{ liste, hauteur: y }], { haut: hautContenu, bas: cyTampon - RAYON - 5, min: 5, max: 14 })
  if (emplacementQr) {
    const COTE = 16, xQ = X0, yQ = cyTampon - COTE / 2 - 2, E = 0.3
    P.rect(xQ, yQ, COTE, E, T.cadre, 'qr')
    P.rect(xQ, yQ + COTE - E, COTE, E, T.cadre, 'qr')
    P.rect(xQ, yQ, E, COTE, T.cadre, 'qr')
    P.rect(xQ + COTE - E, yQ, E, COTE, T.cadre, 'qr')
    P.texte(L('qrAVenir'), { x: xQ, y: yQ + COTE + 3.6, taille: 2.1, couleur: T.muet, role: 'qr-a-venir' })
  }
  dessinerTampon(O, P, { cx: xSig - 34, cy: cyTampon, rayon: RAYON, haut: caps(L('tamponHaut')), bas: caps(L('tamponBas')) })
  blocSignature(P, T, LC, { x: xSig, bas: BAS })
  return { page, fond: T.fond, elements: els, densite, ...placement }
}

/* --------------------------- L'IMAGE DE PARTAGE (LinkedIn, 4:5, portrait)
   La vitrine recomposee pour un format d'ecran : moins de texte, plus
   grand ; pas de bouton ni de logo LinkedIn. v4 du 07/10 (Nawal) :
     - message principal : « a évalué ses soft skills avec les Soft Skills
       Scans de Rising Up » s'il tient sur deux lignes, sinon la version
       courte (« ... avec Rising Up ») ;
     - « MES 3 SOFT SKILLS CLEFS », puis les trois competences : nom d'usage
       et jauge, rien d'autre (ni nom CoreSkills, ni description, ni « Ce que
       tu as gagné ») ;
     - en petit, la phrase de methode des Soft Skills Scans. */
const composerImagePartage = (ctx, densite) => {
  const { O, T, L, LC, caps, prenomNom, nomParcours, dateTexte, contenu, exemple } = ctx
  const page = PAGE_PARTAGE
  const d = densite
  const els = []
  const P = O.fabrique(els)
  const CX = page.largeur / 2, X0 = 20, X1 = page.largeur - 20, LARG = X1 - X0
  fondEtCoins(P, T, page)
  enTeteCommun(P, T, page, { exemple, LC, caps, droite: X1, hLogo: 6.4, yLogo: 15 })

  P.texte(caps(L('mention')), { x: CX, y: 38, taille: 4, couleur: CHARTE.orange, esp: 0.14, ancre: 'milieu', role: 'mention' })
  const LARGEUR_NOM = 176, ESP_NOM = -0.035
  let tNom = 17
  while (tNom > 11 && O.largeur(prenomNom, tNom, ESP_NOM) > LARGEUR_NOM) tNom -= 0.5
  let lignesNom = [String(prenomNom || '')]
  if (O.largeur(prenomNom, tNom, ESP_NOM) > LARGEUR_NOM) lignesNom = couper(prenomNom, tNom, ESP_NOM, LARGEUR_NOM, O.largeur).slice(0, 2)
  let yNom = 38 + 8 + CAPITALE * tNom
  lignesNom.forEach((l, i) => P.texte(l, { x: CX, y: yNom + i * tNom * 1.05, taille: tNom, couleur: T.heros, esp: ESP_NOM, ancre: 'milieu', role: 'nom' }))
  yNom += (lignesNom.length - 1) * tNom * 1.05
  const yFilet = yNom + 6
  P.rect(CX - 7, yFilet, 14, 1, CHARTE.orange, 'filet-nom')
  const T_MESSAGE = 5.6, L_MESSAGE = 170
  const message = O.lignesDe(L('partageMessageLong'), T_MESSAGE, 0, L_MESSAGE).length <= 2 ? L('partageMessageLong') : L('partageMessageCourt')
  const basPhrase = O.paragraphe(P, message, { x: CX, ancre: 'milieu', haut: yFilet + 7, largeurMax: L_MESSAGE, taille: T_MESSAGE, couleur: T.texte, interligne: 1.28, role: 'accomplissement' })
  let hautContenu = basPhrase + 2.5
  if (nomParcours) {
    const yParc = basPhrase + 9.5
    const lEt = O.largeur(caps(L('parcours')), 2.9, ESP_ETIQUETTE)
    const lignesParc = O.lignesDe(nomParcours, 5, -0.02, LARG - lEt - 3)
    ligneCentree(O, P, [
      { t: caps(L('parcours')), taille: 2.9, couleur: T.doux, esp: ESP_ETIQUETTE, role: 'etiquette-parcours' },
      { t: lignesParc.length > 1 ? lignesParc[0] + '…' : lignesParc[0], taille: 5, couleur: T.heros, esp: -0.02, avant: 3, role: 'parcours' },
    ], { cx: CX, y: yParc })
    hautContenu = yParc + 2.5
  }

  /* « MES 3 SOFT SKILLS CLEFS » : nom d'usage et jauge */
  const liste = []
  const D = O.fabrique(liste)
  const yEt = CAPITALE * 2.9
  const lE = D.texte(caps(L('atoutsCles')), { x: CX, y: yEt, taille: 2.9, couleur: CHARTE.orange, esp: ESP_ETIQUETTE, ancre: 'milieu', role: 'etiquette-atouts' })
  if (contenu.provisoire?.atouts) D.texte(L('provisoire'), { x: CX + lE / 2 + 2.5, y: yEt, taille: 2.9, couleur: CHARTE.orange, esp: 0.04, role: 'provisoire' })
  let y = yEt + JAMBAGE * 2.9 + 5.5
  const PAD = 8.5, LJ = 68, HJ = 7
  contenu.atouts.forEach((a, i) => {
    const carte = []
    const C = O.fabrique(carte)
    const tTitre = 7.4 * d
    const yc = O.paragraphe(C, a.nomUsage, { x: X0 + PAD, haut: PAD, largeurMax: LARG - 2 * PAD - LJ - 8, taille: tTitre, couleur: T.heros, interligne: 1.1, esp: -0.02, role: 'atout-titre' })
    const h = Math.max(yc, PAD + HJ) + PAD
    dessinerJauge(C, { x: X1 - PAD - LJ, y: (h - HJ) / 2, largeur: LJ, hauteur: HJ, jauge: a.jauge })
    if (T.carte) {
      D.rect(X0, y, LARG, h, T.cadre, 'carte-cadre')
      D.rect(X0 + 0.25, y + 0.25, LARG - 0.5, h - 0.5, T.carte, 'carte')
    } else {
      D.rect(X0, y, LARG, 0.25, T.cadre, 'carte-cadre')
    }
    O.decaler(carte, y)
    liste.push(...carte)
    y += h + (i < 2 ? 5 : 0)
  })

  /* pied : la phrase de methode, en petit, en bas ; au-dessus, la date, le
     tampon et la signature */
  const T_METHODE = 2.7
  const lignesMethode = lignesEquilibrees(O, L('mentionMethodeCourte'), T_METHODE, 160)
  const yMethode = page.hauteur - 9.5 - (lignesMethode.length - 1) * T_METHODE * 1.35
  lignesMethode.forEach((l, i) => P.texte(l, { x: CX, y: yMethode + i * T_METHODE * 1.35, taille: T_METHODE, couleur: T.doux, ancre: 'milieu', role: 'mention-methode' }))
  const yDate = yMethode - CAPITALE * T_METHODE - 4
  P.texte(caps(LC('delivreLe')) + ' ' + dateTexte, { x: CX, y: yDate, taille: 2.8, couleur: T.doux, esp: 0.04, ancre: 'milieu', role: 'date' })
  const BAS = yDate - CAPITALE * 2.8 - 5
  const xSig = 122
  const RAYON = 17.5
  const cyTampon = BAS - 21
  const placement = placerBlocs(O, els, [{ liste, hauteur: y }], { haut: hautContenu, bas: cyTampon - RAYON - 4, min: 4, max: 14 })
  dessinerTampon(O, P, { cx: xSig - 38, cy: cyTampon, rayon: RAYON, haut: caps(L('tamponHaut')), bas: caps(L('tamponBas')) })
  blocSignature(P, T, LC, { x: xSig, bas: BAS, lSig: 34, k: 1.08 })
  return { page, fond: T.fond, elements: els, densite, ...placement }
}

/* ------------------------------------------------------- l'entree */
const contexte = ({ prenomNom, nomParcours, dateDelivrance, contenu, variante = 'claire', langue = 'fr', exemple = false, mesurer }) => {
  if (typeof mesurer !== 'function') throw new Error('composerBilan\u00a0: il faut une fonction mesurer(texte, tailleMm)')
  if (!contenu || !Array.isArray(contenu.atouts) || contenu.atouts.length !== 3) {
    throw new Error('composerBilan\u00a0: contenu incomplet (3 atouts)')
  }
  const forme = contenu.public === 'professionnel' ? FORME_VOUS : FORME_TU
  const nomVariante = VARIANTES_BILAN[variante] ? variante : 'claire'
  return {
    O: outils(mesurer),
    T: VARIANTES_BILAN[nomVariante],
    variante: nomVariante,
    L: (cle) => libelle(cle, langue, forme),
    LC: (cle) => libelleCertificat(cle, langue),
    caps: (t) => capitales(t, langue),
    prenomNom: String(prenomNom || ''),
    nomParcours: String(nomParcours || ''),
    dateTexte: formaterDate(dateDelivrance, langue) || '',
    contenu,
    exemple,
  }
}
const auMieux = (composer) => {
  let essai = null
  for (const densite of DENSITES) {
    essai = composer(densite)
    if (!essai.deborde) return essai
  }
  return essai
}

/* Les deux pages du bilan (A4 portrait). */
export const composerBilan = (args) => {
  const ctx = contexte(args)
  const vitrine = auMieux((d) => composerVitrine(ctx, d))
  const entretien = auMieux((d) => composerEntretien(ctx, d))
  return { pages: [vitrine, entretien], variante: ctx.variante, date: ctx.dateTexte, deborde: vitrine.deborde || entretien.deborde }
}

/* L'image de partage (216 x 270 mm, soit 1080 x 1350 px a 127 dpi). */
export const composerPartage = (args) => {
  const ctx = contexte(args)
  return { ...auMieux((d) => composerImagePartage(ctx, d)), variante: ctx.variante, date: ctx.dateTexte }
}

/* La page « pour un employeur » (A4 portrait). `emplacementQr` : voir plus
   haut, a retirer tant que le QR n'existe pas. */
export const composerEmployeur = (args) => {
  const ctx = contexte(args)
  return { ...auMieux((d) => composerPageEmployeur(ctx, d, { emplacementQr: !!args.emplacementQr })), variante: ctx.variante, date: ctx.dateTexte }
}
